package com.veldekar.components.srd

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontStyle
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.flexShrink
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontStyle
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.objectFit
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.ArrowDownIcon
import com.varabyte.kobweb.silk.components.icons.ArrowUpIcon
import com.varabyte.kobweb.silk.components.icons.InfoIcon
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.Surface
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.veldekar.HeadlineTextStyle
import com.veldekar.toSitePalette
import kotlin.Boolean
import kotlin.Int
import kotlin.String
import kotlin.Unit
import kotlin.collections.List
import kotlin.collections.Map
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.keywords.auto
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H1

public enum class SpellComponent(
    public val initial: String,
    public val definition: String,
) {
    VERBAL("V", "A verbal component is a spoken incantation. To provide a verbal component, a character must be able to speak in a strong voice."),
    SOMATIC("S", "A somatic component is a measured and precise movement of the hand or some other part of the body. To provide a somatic component, a caster must have at least one hand free."),
    MATERIAL("M", "A material component is a particular object consumed or transformed by the spell's casting, or a focus used to channel the magic. The spell description specifies the required materials."),
    ;
}

@Composable
public fun LabeledData(
    label: String,
    `data`: String,
    modifier: Modifier = Modifier,
) {
    Row(modifier.gap(0.5.cssRem)) {
        SpanText(label + ":", Modifier.fontWeight(700))
        SpanText(data)
    }
}

@Composable
public fun SpellMetadata(
    spellIndex: String,
    castingTime: String,
    range: String,
    components: List<SpellComponent>,
    material: String?,
    duration: String,
    damageBase: String?,
    damageScaling: Map<String, String>?,
    savingThrow: String?,
    aoe: String?,
    concentration: Boolean,
) {
    val palette = ColorMode.current.toSitePalette()
    var componentsExpanded by remember { mutableStateOf(false) }
    var damageExpanded by remember { mutableStateOf(false) }
    Surface(Modifier.fillMaxWidth().backgroundColor(palette.nearBackground).border(1.px, LineStyle.Solid, palette.brand.primary).borderRadius(4.px).padding(1.cssRem)) {
        SimpleGrid(numColumns(base = 1, md = 2), Modifier.fillMaxWidth().gap(0.5.cssRem)) {
            LabeledData("Casting Time", castingTime)
            LabeledData("Range", range)
            Column(Modifier.fillMaxWidth().gap(0.25.cssRem)) {
                Row(Modifier.gap(0.25.cssRem), verticalAlignment = Alignment.CenterVertically) {
                    SpanText("Components:", Modifier.fontWeight(700))
                    val compsText = components.joinToString(", ") { it.initial }
                    SpanText(compsText)
                    InfoIcon(Modifier.cursor(Cursor.Pointer).fontSize(0.8.cssRem).onClick { componentsExpanded = !componentsExpanded })
                }
                if (componentsExpanded) {
                    Surface(Modifier.backgroundColor(palette.nearBackground).border(1.px, LineStyle.Solid, palette.brand.primary).borderRadius(4.px).padding(0.75.cssRem).gap(0.5.cssRem)) {
                        Column(Modifier.gap(0.5.cssRem)) {
                            components.forEach { component ->
                                Column(Modifier.gap(0.25.cssRem)) {
                                    Row(Modifier.gap(0.px), verticalAlignment = Alignment.CenterVertically) {
                                        SpanText(component.initial, Modifier.fontWeight(700))
                                        SpanText(component.name.lowercase().drop(1))
                                    }
                                    SpanText(component.definition, Modifier.fontSize(0.8.cssRem))
                                }
                            }
                        }
                    }
                }
            }
            material?.let { LabeledData("Material", it) }
            LabeledData("Duration", duration)
            damageBase?.let { base ->
                Column(Modifier.fillMaxWidth().gap(0.25.cssRem)) {
                    Row(Modifier.gap(0.25.cssRem), verticalAlignment = Alignment.CenterVertically) {
                        LabeledData("Damage", base)
                        damageScaling?.takeIf { it.isNotEmpty() }?.let { scaling ->
                            InfoIcon(Modifier.cursor(Cursor.Pointer).fontSize(0.8.cssRem).onClick { damageExpanded = !damageExpanded })
                        }
                    }
                    if (damageExpanded) {
                        damageScaling?.let { scaling ->
                            Surface(Modifier.backgroundColor(palette.nearBackground).border(1.px, LineStyle.Solid, palette.brand.primary).borderRadius(4.px).padding(0.75.cssRem).gap(0.5.cssRem)) {
                                SimpleGrid(numColumns(1), Modifier.gap(0.5.cssRem)) {
                                    scaling.entries.forEach { (level, dice) ->
                                        Row(Modifier.gap(0.5.cssRem)) {
                                            SpanText(level + ":", Modifier.fontWeight(700))
                                            SpanText(dice)
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
            savingThrow?.let { LabeledData("Saving Throw", it) }
            aoe?.let { LabeledData("AOE", it) }
            LabeledData("Concentration", if (concentration) "Yes" else "No")
        }
    }
}

@Composable
public fun SpellCardContent(
    name: String,
    subtitle: String,
    veldekar: String,
    image: String,
    imageWidth: Int? = null,
    imageHeight: Int? = null,
    headerExtra: @Composable (() -> Unit)?,
    metadata: @Composable () -> Unit,
    content: @Composable () -> Unit,
) {
    val palette = ColorMode.current.toSitePalette()
    Column(Modifier.fillMaxWidth().gap(1.cssRem)) {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.Top) {
            Row(Modifier.gap(1.cssRem).flexGrow(1f), verticalAlignment = Alignment.CenterVertically) {
                if (image.isNotEmpty()) {
                    Image(if (image.startsWith("/")) image else "/" + image, width = imageWidth, height = imageHeight, modifier = Modifier.width(4.cssRem).height(auto).borderRadius(4.px).objectFit(ObjectFit.Contain).flexShrink(0f))
                }
                Column(Modifier.gap(0.25.cssRem).flexGrow(1f)) {
                    H1(HeadlineTextStyle.toModifier().fontSize(2.cssRem).margin(0.px).toAttrs()) { SpanText(name) }
                    SpanText(subtitle, Modifier.fontStyle(FontStyle.Italic).fontSize(0.9.cssRem))
                    SpanText(veldekar, Modifier.fontSize(0.9.cssRem))
                }
            }
            headerExtra?.invoke()
        }
        Column(Modifier.fillMaxWidth().gap(0.75.cssRem)) {
            metadata()
            Column(Modifier.fillMaxWidth().padding(top = 0.5.cssRem)) {
                content()
            }
        }
    }
}

@Composable
public fun SpellItem(
    name: String,
    subtitle: String,
    veldekar: String,
    image: String,
    imageWidth: Int? = null,
    imageHeight: Int? = null,
    index: String,
    expanded: Boolean,
    onToggle: () -> Unit,
    metadata: @Composable () -> Unit,
    content: @Composable () -> Unit,
) {
    val palette = ColorMode.current.toSitePalette()
    Column(Modifier.fillMaxWidth().backgroundColor(palette.nearBackground).borderRadius(8.px).padding(1.cssRem).gap(1.cssRem)) {
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.SpaceBetween) {
            Row(Modifier.gap(1.cssRem).flexGrow(1f), verticalAlignment = Alignment.CenterVertically) {
                if (image.isNotEmpty()) {
                    Image(if (image.startsWith("/")) image else "/" + image, width = imageWidth, height = imageHeight, modifier = Modifier.width(4.cssRem).height(auto).borderRadius(4.px).objectFit(ObjectFit.Contain).flexShrink(0f))
                }
                Column(Modifier.gap(0.25.cssRem).flexGrow(1f)) {
                    Link("/spell-" + index) {
                        val nameSize = if (expanded) 2.cssRem else 1.2.cssRem
                        SpanText(name, Modifier.fontSize(nameSize).fontWeight(700))
                    }
                    if (expanded) SpanText(subtitle, Modifier.fontStyle(FontStyle.Italic).fontSize(0.9.cssRem))
                    SpanText(veldekar, Modifier.fontSize(0.9.cssRem))
                }
            }
            Row(Modifier.cursor(Cursor.Pointer).onClick { onToggle() }, verticalAlignment = Alignment.CenterVertically) {
                if (expanded) ArrowUpIcon() else ArrowDownIcon()
            }
        }
        if (expanded) {
            Column(Modifier.fillMaxWidth().padding(top = 1.cssRem)) {
                Column(Modifier.fillMaxWidth().gap(0.75.cssRem)) {
                    metadata()
                    Column(Modifier.fillMaxWidth().padding(top = 0.5.cssRem)) {
                        content()
                    }
                }
            }
        }
    }
}
